import { SuperSearchResult } from 'api';
import { SUPER_SEARCH_COMMANDS } from './SuperSearch.constants';
import type {
  SuperSearchContext,
  SuperSearchDisplayItem,
  SuperSearchHistoryDestination,
  SuperSearchHistoryEntry,
  SuperSearchHistoryState,
} from './SuperSearch.types';

export const SUPER_SEARCH_HISTORY_LIMIT = 25;
export const SUPER_SEARCH_HISTORY_VERSION = 1;
export const SUPER_SEARCH_QUICK_ACCESS_LIMIT = 6;

const DAY_IN_MS = 24 * 60 * 60 * 1_000;
const RECENCY_DECAY_DAYS = 14;
const NON_RECONSTRUCTABLE_HISTORY_RESULT_TYPES = new Set<SuperSearchResult.type>([
  SuperSearchResult.type.CAP_TABLE_VERSION,
  SuperSearchResult.type.CUSTOM_VIEW,
  SuperSearchResult.type.DOCUMENT,
  SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION,
  SuperSearchResult.type.VALUATION_APPROACH,
  SuperSearchResult.type.VALUATION_VERSION,
  SuperSearchResult.type.WATERFALL_VIEW,
]);

export const createEmptySuperSearchHistory = (): SuperSearchHistoryState => ({
  entries: [],
  version: SUPER_SEARCH_HISTORY_VERSION,
});

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const normalizeEntry = (value: unknown): SuperSearchHistoryEntry | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const entry = value as Partial<SuperSearchHistoryEntry>;
  if (
    typeof entry.destinationKey !== 'string' ||
    !entry.destinationKey.trim() ||
    !isFiniteNumber(entry.firmId) ||
    !isFiniteNumber(entry.lastUsedAt) ||
    !isFiniteNumber(entry.useCount) ||
    entry.useCount < 1 ||
    (entry.companyId !== undefined && !isFiniteNumber(entry.companyId))
  ) {
    return undefined;
  }

  return {
    ...(entry.companyId === undefined ? {} : { companyId: entry.companyId }),
    destinationKey: entry.destinationKey,
    firmId: entry.firmId,
    lastUsedAt: entry.lastUsedAt,
    useCount: Math.floor(entry.useCount),
  };
};

export const normalizeSuperSearchHistory = (value: unknown): SuperSearchHistoryState => {
  if (!value || typeof value !== 'object') {
    return createEmptySuperSearchHistory();
  }

  const state = value as Partial<SuperSearchHistoryState>;
  if (state.version !== SUPER_SEARCH_HISTORY_VERSION || !Array.isArray(state.entries)) {
    return createEmptySuperSearchHistory();
  }

  const entriesByKey = new Map<string, SuperSearchHistoryEntry>();
  state.entries.forEach(valueEntry => {
    const entry = normalizeEntry(valueEntry);
    if (!entry) {
      return;
    }

    const existing = entriesByKey.get(entry.destinationKey);
    if (!existing || entry.lastUsedAt > existing.lastUsedAt) {
      entriesByKey.set(entry.destinationKey, entry);
    }
  });

  return {
    entries: Array.from(entriesByKey.values())
      .sort((left, right) => right.lastUsedAt - left.lastUsedAt)
      .slice(0, SUPER_SEARCH_HISTORY_LIMIT),
    version: SUPER_SEARCH_HISTORY_VERSION,
  };
};

export const recordSuperSearchDestination = (
  value: unknown,
  destination: SuperSearchHistoryDestination,
  now = Date.now()
): SuperSearchHistoryState => {
  const history = normalizeSuperSearchHistory(value);
  const existing = history.entries.find(entry => entry.destinationKey === destination.destinationKey);
  const nextEntry: SuperSearchHistoryEntry = {
    ...destination,
    lastUsedAt: now,
    useCount: (existing?.useCount ?? 0) + 1,
  };

  return {
    entries: [nextEntry, ...history.entries.filter(entry => entry.destinationKey !== destination.destinationKey)].slice(
      0,
      SUPER_SEARCH_HISTORY_LIMIT
    ),
    version: SUPER_SEARCH_HISTORY_VERSION,
  };
};

export const getSuperSearchHistoryDestination = (
  item: SuperSearchDisplayItem
): SuperSearchHistoryDestination | undefined => {
  if (item.kind === 'company-page-command' || item.kind === 'firm-page-command') {
    return undefined;
  }

  if (NON_RECONSTRUCTABLE_HISTORY_RESULT_TYPES.has(item.result.type)) {
    return undefined;
  }

  const { company, firm } = item.result;
  if (item.companyAction && !company) {
    return undefined;
  }

  let destinationKey = item.id;
  if (item.companyAction && company) {
    destinationKey = `company-action:${item.companyAction}:firm:${firm.id}:company:${company.id}`;
  } else if (item.command === SUPER_SEARCH_COMMANDS.OPEN_NOTIFICATIONS) {
    destinationKey = `command:${item.command}`;
  } else if (item.command === SUPER_SEARCH_COMMANDS.START_GUIDED_TOUR && item.tourId) {
    destinationKey = `guided-tour:${item.tourId}`;
  }

  return {
    ...(company ? { companyId: company.id } : {}),
    destinationKey,
    firmId: firm.id,
  };
};

const getContextRank = (entry: SuperSearchHistoryEntry, context: SuperSearchContext) => {
  if (context.company) {
    if (entry.companyId === context.company.id) {
      return 0;
    }

    return entry.firmId === context.firm?.id ? 1 : 2;
  }

  if (context.firm) {
    return entry.firmId === context.firm.id ? 0 : 1;
  }

  return 0;
};

const getHistoryScore = (entry: SuperSearchHistoryEntry, maxUseCount: number, now: number) => {
  const ageDays = Math.max(0, now - entry.lastUsedAt) / DAY_IN_MS;
  const recencyScore = 1 / (1 + ageDays / RECENCY_DECAY_DAYS);
  const frequencyScore = Math.log1p(entry.useCount) / Math.log1p(Math.max(1, maxUseCount));
  return recencyScore * 0.7 + frequencyScore * 0.3;
};

const isPinnedEntity = (
  item: SuperSearchDisplayItem,
  pinnedCompanyIds: ReadonlySet<number>,
  pinnedFirmIds: ReadonlySet<number>
) => {
  if (item.result.type === SuperSearchResult.type.COMPANY) {
    return Boolean(item.result.company && pinnedCompanyIds.has(item.result.company.id));
  }

  return item.result.type === SuperSearchResult.type.FIRM && pinnedFirmIds.has(item.result.firm.id);
};

const isRedundantCurrentEntity = (item: SuperSearchDisplayItem, context: SuperSearchContext) => {
  if (context.company && item.result.type === SuperSearchResult.type.COMPANY) {
    return item.result.company?.id === context.company.id;
  }

  if (context.firm && !context.company && item.result.type === SuperSearchResult.type.FIRM) {
    return item.result.firm.id === context.firm.id;
  }

  return false;
};

const isAvailableInQuickAccessContext = (item: SuperSearchDisplayItem, context: SuperSearchContext) =>
  !context.firm || item.result.firm.id === context.firm.id;

const appendUniqueItems = (
  destination: SuperSearchDisplayItem[],
  items: SuperSearchDisplayItem[],
  seenDestinationKeys: Set<string>,
  limit: number
) => {
  items.some(item => {
    const key = getSuperSearchHistoryDestination(item)?.destinationKey ?? item.id;
    if (!seenDestinationKeys.has(key)) {
      seenDestinationKeys.add(key);
      destination.push(item);
    }

    return destination.length >= limit;
  });
};

export const getSuperSearchQuickAccessItems = ({
  candidates,
  context,
  fallbackItems,
  history: rawHistory,
  now = Date.now(),
  pinnedCompanyIds,
  pinnedFirmIds,
}: {
  candidates: SuperSearchDisplayItem[];
  context: SuperSearchContext;
  fallbackItems: SuperSearchDisplayItem[];
  history: unknown;
  now?: number;
  pinnedCompanyIds: ReadonlySet<number>;
  pinnedFirmIds: ReadonlySet<number>;
}): SuperSearchDisplayItem[] => {
  const history = normalizeSuperSearchHistory(rawHistory);
  const entriesByKey = new Map(history.entries.map(entry => [entry.destinationKey, entry]));
  const availableCandidates = candidates.filter(
    item => isAvailableInQuickAccessContext(item, context) && !isRedundantCurrentEntity(item, context)
  );
  const pinnedItems = availableCandidates.filter(item => isPinnedEntity(item, pinnedCompanyIds, pinnedFirmIds));
  const resolvedHistoryItems = availableCandidates.flatMap(item => {
    const destination = getSuperSearchHistoryDestination(item);
    const entry = destination ? entriesByKey.get(destination.destinationKey) : undefined;
    return entry ? [{ entry, item }] : [];
  });
  const maxUseCount = Math.max(1, ...resolvedHistoryItems.map(({ entry }) => entry.useCount));
  const historyItems = resolvedHistoryItems.sort((left, right) => {
    const contextDifference = getContextRank(left.entry, context) - getContextRank(right.entry, context);
    if (contextDifference !== 0) {
      return contextDifference;
    }

    const scoreDifference =
      getHistoryScore(right.entry, maxUseCount, now) - getHistoryScore(left.entry, maxUseCount, now);
    return scoreDifference || right.entry.lastUsedAt - left.entry.lastUsedAt;
  });
  const currentCompanyHistory = context.company
    ? historyItems.filter(({ entry }) => entry.companyId === context.company?.id).map(({ item }) => item)
    : [];
  const remainingHistory = historyItems
    .filter(({ entry }) => !context.company || entry.companyId !== context.company.id)
    .map(({ item }) => item);
  const quickAccessItems: SuperSearchDisplayItem[] = [];
  const seenDestinationKeys = new Set<string>();

  if (context.company) {
    appendUniqueItems(quickAccessItems, currentCompanyHistory, seenDestinationKeys, SUPER_SEARCH_QUICK_ACCESS_LIMIT);
  }
  appendUniqueItems(quickAccessItems, pinnedItems, seenDestinationKeys, SUPER_SEARCH_QUICK_ACCESS_LIMIT);
  appendUniqueItems(
    quickAccessItems,
    context.company ? remainingHistory : historyItems.map(({ item }) => item),
    seenDestinationKeys,
    SUPER_SEARCH_QUICK_ACCESS_LIMIT
  );
  appendUniqueItems(
    quickAccessItems,
    fallbackItems.filter(
      item => isAvailableInQuickAccessContext(item, context) && !isRedundantCurrentEntity(item, context)
    ),
    seenDestinationKeys,
    SUPER_SEARCH_QUICK_ACCESS_LIMIT
  );

  return quickAccessItems;
};
